import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ArrowLeft, Save, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../services/api';

const AdminBandMessage = () => {
    const [form, setForm] = useState({
        title: 'Message from the Band',
        body: '',
        author: 'The Deep Wave',
        is_active: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null); // { type: 'success'|'error', text: string }
    const [preview, setPreview] = useState(false);

    useEffect(() => {
        api.get('/admin/band-message')
            .then(res => {
                if (res.data.success && res.data.data) {
                    const m = res.data.data;
                    setForm({
                        title: m.title || 'Message from the Band',
                        body: m.body || '',
                        author: m.author || 'The Deep Wave',
                        is_active: !!m.is_active
                    });
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setResult(null);
    };

    const handleSave = async () => {
        if (!form.body.trim()) {
            setResult({ type: 'error', text: 'Message body is required.' });
            return;
        }
        setSaving(true);
        setResult(null);
        try {
            const res = await api.put('/admin/band-message', form);
            if (res.data.success) {
                setResult({ type: 'success', text: 'Message saved successfully.' });
            } else {
                setResult({ type: 'error', text: res.data.message || 'Failed to save.' });
            }
        } catch (err) {
            setResult({ type: 'error', text: err.response?.data?.message || 'Failed to save.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <MessageSquare className="w-10 h-10 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-3xl mx-auto px-6">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Link
                        to="/admin/dashboard"
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-400" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <MessageSquare className="w-7 h-7 text-blue-400" />
                        <h1 className="text-3xl font-black gradient-text">Message from the Band</h1>
                    </div>
                </div>

                {/* Form card */}
                <div className="glass-card p-6 sm:p-8 space-y-6">
                    {/* Active toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                        <div>
                            <p className="text-white font-semibold">Show on homepage</p>
                            <p className="text-gray-400 text-sm">When off the widget is hidden from visitors</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={form.is_active}
                                onChange={handleChange}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Heading
                        </label>
                        <input
                            type="text"
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            placeholder="Message from the Band"
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                        />
                    </div>

                    {/* Body */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Message <span className="text-red-400">*</span>
                        </label>
                        <textarea
                            name="body"
                            value={form.body}
                            onChange={handleChange}
                            placeholder="Write your message to fans here..."
                            rows={10}
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-y leading-relaxed"
                        />
                        <p className="text-gray-500 text-xs mt-1">Line breaks are preserved as-is.</p>
                    </div>

                    {/* Author */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Author / Sign-off
                        </label>
                        <input
                            type="text"
                            name="author"
                            value={form.author}
                            onChange={handleChange}
                            placeholder="The Deep Wave"
                            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                        />
                        <p className="text-gray-500 text-xs mt-1">Displayed as "— Author" below the message. Leave blank to omit.</p>
                    </div>

                    {/* Result banner */}
                    {result && (
                        <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                            result.type === 'success'
                                ? 'bg-green-600/10 border-green-600/30 text-green-400'
                                : 'bg-red-600/10 border-red-600/30 text-red-400'
                        }`}>
                            {result.type === 'success'
                                ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                                : <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            }
                            <span>{result.text}</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between gap-4 pt-2">
                        <button
                            type="button"
                            onClick={() => setPreview(v => !v)}
                            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                            {preview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            {preview ? 'Hide preview' : 'Preview'}
                        </button>

                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving…' : 'Save Message'}
                        </button>
                    </div>
                </div>

                {/* Preview */}
                {preview && (
                    <div className="mt-8">
                        <p className="text-gray-400 text-sm mb-3 uppercase tracking-widest">Preview</p>
                        <div className="glass-card p-8 sm:p-12 border border-blue-600/30 relative overflow-hidden">
                            <div className="absolute top-4 left-6 text-blue-600/20 text-[8rem] leading-none font-serif select-none pointer-events-none" aria-hidden="true">
                                &ldquo;
                            </div>
                            <div className="relative z-10">
                                <h2 className="text-2xl sm:text-3xl font-black gradient-text mb-6">
                                    {form.title || 'Message from the Band'}
                                </h2>
                                <p className="text-gray-300 text-base sm:text-lg leading-relaxed whitespace-pre-wrap">
                                    {form.body || <span className="italic text-gray-500">Your message will appear here…</span>}
                                </p>
                                {form.author && (
                                    <p className="mt-6 text-blue-400 font-semibold text-sm sm:text-base">
                                        — {form.author}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminBandMessage;
